/**
 * Player Profile — maps handicap (0-25) to swing characteristic scaling.
 *
 * PGA Tour averages are the "ceiling." Handicap scaling brings them down
 * to realistic amateur baselines calibrated against Arccos / Shot Scope data.
 *
 * Key amateur trends as handicap increases:
 *   - Club speed drops (~97% at scratch → ~73% at 25 hcp)
 *   - Smash factor degrades (worse center contact)
 *   - Launch angle increases (more dynamic loft / scoop)
 *   - Driver spin INCREASES (more loft at impact, open face)
 *   - Iron spin DECREASES (less compression, thin strikes)
 *   - Attack angle becomes shallower (less downward strike)
 */

import type { ClubPreset } from '../types';

export interface HandicapProfile {
  clubSpeedScale: number;      // multiplier on tour club speed
  smashScale: number;          // multiplier on tour smash factor
  launchOffset: number;        // additive degrees to launch angle
  driverSpinScale: number;     // multiplier on driver/wood spin
  ironSpinScale: number;       // multiplier on iron/wedge spin
  attackAngleOffset: number;   // additive degrees (positive = shallower)
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Get swing characteristic profile for a given handicap.
 *
 * Calibrated against published amateur data:
 *   HCP  0 → ~110 mph driver, 87 mph 7i, smash ~1.47/1.33
 *   HCP 10 → ~97 mph driver,  80 mph 7i, smash ~1.44/1.30
 *   HCP 15 → ~93 mph driver,  74 mph 7i, smash ~1.41/1.27
 *   HCP 25 → ~83 mph driver,  66 mph 7i, smash ~1.38/1.23
 */
export function getHandicapProfile(handicap: number): HandicapProfile {
  const t = Math.max(0, Math.min(25, handicap)) / 25;

  return {
    clubSpeedScale:    lerp(0.97, 0.73, t),
    smashScale:        lerp(0.995, 0.93, t),
    launchOffset:      lerp(0.5, 3.0, t),
    driverSpinScale:   lerp(1.02, 1.50, t),
    ironSpinScale:     lerp(0.99, 0.78, t),
    attackAngleOffset: lerp(0.5, 4.0, t),
  };
}

/**
 * Scale a PGA Tour club preset to amateur baseline for a given handicap.
 *
 * Returns a modified ClubPreset with adjusted club speed, ball speed,
 * launch angle, spin rate, and attack angle. Other fields (name, spin axis,
 * club path, face angle) pass through unchanged.
 */
export function scalePreset(
  preset: ClubPreset,
  clubKey: string,
  profile: HandicapProfile,
): ClubPreset {
  const isDriverWood = clubKey === 'driver' || clubKey === '3wood';

  // Scale club speed
  const clubSpeed = Math.round(preset.clubSpeed * profile.clubSpeedScale);

  // Dynamic smash factor: starts from tour smash, degrades with handicap contact quality,
  // and adjusts for attack angle efficiency (optimal driver AoA ≈ +3° for max smash).
  const tourSmash = preset.ballSpeed / preset.clubSpeed;
  let smash = tourSmash * profile.smashScale;

  // Attack-angle efficiency: for driver/woods, optimal AoA is +2-4°.
  // Too negative (chopping) or too steep up loses smash.
  if (isDriverWood) {
    const effectiveAoA = preset.attackAngle + profile.attackAngleOffset;
    const optimalAoA = 3.0; // degrees up
    const aoaDeviation = Math.abs(effectiveAoA - optimalAoA);
    smash *= 1 - aoaDeviation * 0.003; // ~0.3% per degree off-optimal
  }

  const ballSpeed = Math.round(clubSpeed * smash);

  // Offset launch angle (higher handicap = more dynamic loft)
  const launchAngle = Math.round((preset.launchAngle + profile.launchOffset) * 10) / 10;

  // Scale spin: driver spin UP, iron spin DOWN with handicap
  const spinScale = isDriverWood ? profile.driverSpinScale : profile.ironSpinScale;
  const spinRate = Math.round((preset.spinRate * spinScale) / 50) * 50;

  // Shallower attack angle (less negative / more positive)
  const attackAngle = Math.round((preset.attackAngle + profile.attackAngleOffset) * 10) / 10;

  return {
    ...preset,
    clubSpeed,
    ballSpeed,
    launchAngle,
    spinRate,
    attackAngle,
  };
}
